/* ==========================================================================
   Dutturi — JS vanilla do tema
   Módulos: reveal, header/mega, busca preditiva, sacola, favoritos,
   formulário de produto + bordado, filtros de coleção, utilidades das seções.
   ========================================================================== */
(() => {
  'use strict';

  const D = window.Dutturi || {};
  const S = D.strings || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Utilidades ---------- */
  const money = (cents) => {
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: D.currency || 'BRL' }).format(cents / 100);
    } catch (e) {
      return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',');
    }
  };

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  let toastTimer;
  const toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 2600);
  };

  const lockScroll = (on) => document.body.classList.toggle('is-locked', on);

  const htmlFrom = (text) => new DOMParser().parseFromString(text, 'text/html');

  const isEmail = (v) => /^\S+@\S+\.\S+$/.test((v || '').trim());
  const digits = (v) => (v || '').replace(/\D/g, '');

  /* ---------- Reveal ---------- */
  const initReveal = (root = document) => {
    const els = $$('[data-reveal]:not(.is-revealed)', root);
    if (!els.length) return;
    els.forEach((el) => el.style.setProperty('--reveal-i', parseInt(el.dataset.reveal, 10) || 1));
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));

    // Rede de segurança: se o observer não disparar (webviews, abas em segundo plano),
    // revela pela posição na tela para o conteúdo nunca ficar invisível.
    const check = () => {
      let pending = 0;
      els.forEach((el) => {
        if (el.classList.contains('is-revealed')) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0 && r.width > 0) {
          el.classList.add('is-revealed');
          io.unobserve(el);
        } else pending++;
      });
      if (!pending) window.removeEventListener('scroll', onScroll);
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { ticking = false; check(); });
    };
    setTimeout(() => { check(); window.addEventListener('scroll', onScroll, { passive: true }); }, 600);
  };

  /* ---------- Header: mega menu + menu mobile ---------- */
  const initHeader = () => {
    const header = $('[data-header]');
    if (!header) return;
    const triggers = $$('[data-mega-trigger]', header);
    const panels = $$('[data-mega]', header);

    const close = () => {
      panels.forEach((p) => { p.hidden = true; });
      triggers.forEach((t) => { t.classList.remove('is-open'); t.setAttribute('aria-expanded', 'false'); });
    };
    const open = (id) => {
      panels.forEach((p) => { p.hidden = p.dataset.mega !== id; });
      triggers.forEach((t) => {
        const on = t.dataset.megaTrigger === id;
        t.classList.toggle('is-open', on);
        t.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    };

    triggers.forEach((t) => {
      t.addEventListener('mouseenter', () => open(t.dataset.megaTrigger));
      t.addEventListener('focus', () => open(t.dataset.megaTrigger));
    });
    $$('[data-mega-off]', header).forEach((el) => el.addEventListener('mouseenter', close));
    header.addEventListener('mouseleave', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    header.addEventListener('focusout', (e) => { if (!header.contains(e.relatedTarget)) close(); });

    const toggle = $('[data-menu-toggle]', header);
    const menu = $('[data-mobile-menu]', header);
    if (toggle && menu) {
      toggle.addEventListener('click', () => {
        const willOpen = menu.hidden;
        menu.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        toggle.textContent = willOpen ? toggle.dataset.labelClose : toggle.dataset.labelOpen;
      });
    }
  };

  /* ---------- Busca preditiva ---------- */
  const initSearch = () => {
    const overlay = $('[data-search-overlay]');
    if (!overlay) return;
    const input = $('[data-search-input]', overlay);
    const results = $('[data-search-results]', overlay);
    const idle = $('[data-search-idle]', overlay);
    let controller;

    const openSearch = () => {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      setTimeout(() => input && input.focus(), 30);
    };
    const closeSearch = () => {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      lockScroll(false);
    };

    const run = debounce(async (q) => {
      if (!q) { results.innerHTML = ''; idle.hidden = false; return; }
      idle.hidden = true;
      if (controller) controller.abort();
      controller = new AbortController();
      const url = `${D.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=4&resources[options][prefix]=last&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = htmlFrom(await res.text());
        const section = doc.querySelector('#shopify-section-predictive-search');
        results.innerHTML = section ? section.innerHTML : '';
        Wishlist.paint(results);
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    }, 220);

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-search-open]');
      if (opener) { e.preventDefault(); openSearch(); return; }
      if (e.target.closest('[data-search-close]') || e.target === overlay) closeSearch();
      const term = e.target.closest('[data-search-term]');
      if (term) { input.value = term.dataset.searchTerm; run(input.value.trim()); input.focus(); }
    });
    input.addEventListener('input', () => run(input.value.trim()));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeSearch();
    });
  };

  /* ---------- Sacola ---------- */
  const Cart = {
    overlay: null,
    lastFocus: null,

    init() {
      this.overlay = $('[data-drawer-overlay]');
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener && this.overlay && D.cartDrawer !== false && document.body.classList.contains('template-cart') === false) {
          e.preventDefault();
          this.open();
          return;
        }
        if (!this.overlay) return;
        if (e.target === this.overlay || e.target.closest('[data-drawer-close]')) this.close();
      });
      document.addEventListener('click', (e) => {
        const line = e.target.closest('[data-line]');
        if (!line) return;
        const qtyBtn = e.target.closest('[data-qty-change]');
        const remove = e.target.closest('[data-line-remove]');
        if (!qtyBtn && !remove) return;
        const current = parseInt(line.dataset.qty, 10) || 0;
        const next = remove ? 0 : Math.max(0, current + parseInt(qtyBtn.dataset.qtyChange, 10));
        this.setLine(line, next);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay && this.overlay.classList.contains('is-open')) this.close();
      });
    },

    open() {
      if (!this.overlay) { window.location.href = D.routes.cart; return; }
      this.lastFocus = document.activeElement;
      this.overlay.classList.add('is-open');
      lockScroll(true);
      const drawer = $('[data-drawer]', this.overlay);
      setTimeout(() => drawer && drawer.focus(), 50);
    },

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove('is-open');
      lockScroll(false);
      if (this.lastFocus) this.lastFocus.focus();
    },

    renderSections(sections) {
      if (!sections) return;
      const html = sections['cart-drawer'];
      if (html && this.overlay) {
        const doc = htmlFrom(html);
        const fresh = doc.querySelector('[data-drawer]');
        const drawer = $('[data-drawer]', this.overlay);
        if (fresh && drawer) { drawer.innerHTML = fresh.innerHTML; initShipCalc(drawer); }
      }
      const pageEl = $('[data-cart-page]');
      const page = pageEl ? sections[pageEl.dataset.sectionId] : null;
      if (page) {
        const target = $('[data-cart-page]');
        const doc = htmlFrom(page);
        const fresh = doc.querySelector('[data-cart-page]');
        if (target && fresh) { target.innerHTML = fresh.innerHTML; initShipCalc(target); }
      }
    },

    sectionsParam() {
      const list = ['cart-drawer'];
      const pageEl = $('[data-cart-page]');
      if (pageEl && pageEl.dataset.sectionId) list.push(pageEl.dataset.sectionId);
      return list;
    },

    async refreshCount(cart) {
      const data = cart || await fetch(`${D.routes.cart}.js`).then((r) => r.json());
      const embId = D.embroidery && D.embroidery.variantId;
      const count = data.items.reduce((acc, item) => acc + (item.variant_id === embId ? 0 : item.quantity), 0);
      $$('[data-cart-count]').forEach((el) => { el.textContent = count; });
      return data;
    },

    async add(items) {
      const res = await fetch(`${D.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.sectionsParam(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.cartError);
      this.renderSections(data.sections);
      await this.refreshCount();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    async setLine(line, qty) {
      const updates = { [line.dataset.key]: qty };
      if (line.dataset.embKey) updates[line.dataset.embKey] = qty;
      const drawer = line.closest('[data-drawer]');
      if (drawer) drawer.classList.add('is-busy');
      try {
        const res = await fetch(`${D.routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: this.sectionsParam(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || S.cartError);
        this.renderSections(data.sections);
        await this.refreshCount(data);
      } catch (err) {
        toast(err.message || S.cartError);
      } finally {
        const d = $('[data-drawer]');
        if (d) d.classList.remove('is-busy');
      }
    },

    // POST genérico (update.js / change.js) que já devolve as seções renderizadas
    async post(endpoint, body) {
      const res = await fetch(`${endpoint}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionsParam(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.cartError);
      this.renderSections(data.sections);
      await this.refreshCount(data);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    busy(on) {
      $$('[data-drawer], [data-cart-page]').forEach((el) => el.classList.toggle('is-busy', on));
    },

    // Sugestões, cupom e bordado na sacola (delegação: a sacola é re-renderizada a cada mudança)
    initExtras() {
      document.addEventListener('click', async (e) => {
        // Sugestão para o frete grátis
        const upsell = e.target.closest('[data-upsell-add]');
        if (upsell) {
          const item = upsell.closest('[data-upsell-item]');
          const id = Number($('[data-upsell-variant]', item).value);
          if (!id) return;
          upsell.disabled = true;
          upsell.textContent = S.adding;
          try { await this.add([{ id, quantity: 1 }]); } catch (err) { toast(err.message || S.cartError); upsell.disabled = false; upsell.textContent = S.addShort; }
          return;
        }

        // Remover cupom
        const rm = e.target.closest('[data-discount-remove]');
        if (rm) {
          const box = rm.closest('[data-cart-discount]');
          const codes = (box.dataset.codes || '').split(',').map((c) => c.trim()).filter((c) => c && c.toLowerCase() !== rm.dataset.discountRemove.toLowerCase());
          this.busy(true);
          try { await this.post(D.routes.cartUpdate, { discount: codes.join(',') }); } catch (err) { toast(err.message || S.cartError); } finally { this.busy(false); }
          return;
        }

        // Bordado na sacola: abrir
        const embToggle = e.target.closest('[data-line-emb-toggle]');
        if (embToggle) {
          const form = $('[data-line-emb-form]', embToggle.closest('[data-line-emb]'));
          form.hidden = !form.hidden;
          embToggle.setAttribute('aria-expanded', String(!form.hidden));
          if (!form.hidden) $('[data-line-emb-name]', form).focus();
          return;
        }

        // Bordado na sacola: salvar
        const embSave = e.target.closest('[data-line-emb-save]');
        if (embSave) {
          const line = embSave.closest('[data-line]');
          const form = embSave.closest('[data-line-emb-form]');
          const name = $('[data-line-emb-name]', form).value.trim();
          const err = $('[data-line-emb-error]', form);
          if (!name) { err.hidden = false; $('[data-line-emb-name]', form).focus(); return; }
          if (!D.embroidery || !D.embroidery.variantId) { toast(S.cartError); return; }
          const qty = parseInt(line.dataset.qty, 10) || 1;
          const bid = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
          const title = ($('.line__name', line) || {}).textContent || '';
          embSave.disabled = true;
          this.busy(true);
          try {
            await this.post(D.routes.cartChange, {
              id: line.dataset.key,
              quantity: qty,
              properties: {
                'Bordado – Nome': name,
                'Bordado – Registro': $('[data-line-emb-reg]', form).value.trim(),
                'Bordado – Linha': $('[data-line-emb-thread]', form).value,
                'Bordado – Fonte': $('[data-line-emb-font]', form).value,
                ...($('[data-line-emb-side]', form) ? { 'Bordado – Lado': $('[data-line-emb-side]', form).value } : {}),
                _bordado_id: bid
              }
            });
            await this.add([{ id: D.embroidery.variantId, quantity: qty, properties: { _bordado_id: bid, _peca: title.trim() } }]);
          } catch (error) {
            toast(error.message || S.cartError);
          } finally {
            this.busy(false);
          }
        }
      });

      document.addEventListener('input', (e) => {
        if (e.target.matches('[data-line-emb-name]')) {
          const err = $('[data-line-emb-error]', e.target.closest('[data-line-emb-form]'));
          if (err) err.hidden = true;
        }
      });

      // Aplicar cupom
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('[data-discount-form]');
        if (!form) return;
        e.preventDefault();
        const box = form.closest('[data-cart-discount]');
        const input = $('[data-discount-input]', form);
        const errEl = $('[data-discount-error]', box);
        const code = input.value.trim();
        if (!code) { input.focus(); return; }
        const current = (box.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
        if (current.some((c) => c.toLowerCase() === code.toLowerCase())) { input.value = ''; return; }
        this.busy(true);
        try {
          const data = await this.post(D.routes.cartUpdate, { discount: [...current, code].join(',') });
          const found = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
          if (found && !found.applicable) {
            // a Shopify guarda o código mesmo inválido: remove e avisa
            await this.post(D.routes.cartUpdate, { discount: current.join(',') });
            // a sacola foi re-renderizada: mostra o erro nos campos novos
            $$('[data-cart-discount]').forEach((fresh) => {
              const el = $('[data-discount-error]', fresh);
              const inp = $('[data-discount-input]', fresh);
              const details = $('details', fresh);
              if (el) { el.textContent = S.discountInvalid.replace('[codigo]', code); el.hidden = false; }
              if (inp) inp.value = code;
              if (details) details.open = true;
            });
          }
        } catch (err) {
          if (errEl) { errEl.textContent = err.message || S.cartError; errEl.hidden = false; }
        } finally {
          this.busy(false);
        }
      });
    }
  };

  /* ---------- Favoritos (localStorage + metafield do cliente) ---------- */
  const Wishlist = {
    key: 'dutturi:wishlist',

    read() {
      try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { return []; }
    },

    write(list) {
      try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) { /* modo privado */ }
      this.sync(list);
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
    },

    init() {
      const meta = D.wishlist && Array.isArray(D.wishlist.meta) ? D.wishlist.meta : [];
      if (D.customer && meta.length) {
        const merged = Array.from(new Set([...this.read(), ...meta]));
        try { localStorage.setItem(this.key, JSON.stringify(merged)); } catch (e) { /* noop */ }
      }
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wish-toggle]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        this.toggle(btn.dataset.wishToggle);
      });
      document.addEventListener('wishlist:change', () => this.paint());
      this.paint();
    },

    has(handle) { return this.read().includes(handle); },

    toggle(handle) {
      const list = this.read();
      const on = !list.includes(handle);
      this.write(on ? [...list, handle] : list.filter((h) => h !== handle));
      toast(on ? S.wishAdded : S.wishRemoved);
    },

    paint(root = document) {
      const list = this.read();
      $$('[data-wish-toggle]', root).forEach((btn) => {
        const on = list.includes(btn.dataset.wishToggle);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (btn.hasAttribute('data-wish-label')) btn.textContent = on ? S.wishSaved : S.wishAdd;
      });
      $$('[data-wish-count]').forEach((el) => { el.textContent = list.length; });
    },

    sync(list) {
      const url = D.wishlist && D.wishlist.syncUrl;
      if (!url || !D.customer) return;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: D.customer, wishlist: list })
      }).catch(() => {});
    }
  };

  /* ---------- Compra rápida no card ---------- */
  const initQuickAdd = () => {
    const closeAll = (except) => {
      $$('[data-quick-panel]').forEach((panel) => {
        if (panel === except) return;
        panel.hidden = true;
        const btn = $('[data-quick-open]', panel.closest('.card__media-wrap'));
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    };

    document.addEventListener('click', async (e) => {
      const opener = e.target.closest('[data-quick-open]');
      if (opener) {
        e.preventDefault();
        const panel = $('[data-quick-panel]', opener.closest('.card__media-wrap'));
        const open = panel.hidden;
        closeAll(panel);
        panel.hidden = !open;
        opener.setAttribute('aria-expanded', String(open));
        if (open) { const first = $('[data-quick-add]:not([disabled])', panel); if (first) first.focus(); }
        return;
      }

      const add = e.target.closest('[data-quick-add]');
      if (add) {
        e.preventDefault();
        if (add.disabled || add.classList.contains('is-busy')) return;
        add.classList.add('is-busy');
        try {
          await Cart.add([{ id: Number(add.dataset.quickAdd), quantity: 1 }]);
          closeAll();
          if (D.cartDrawer !== false) Cart.open();
          else window.location.href = D.routes.cart;
        } catch (err) {
          toast(err.message || S.cartError);
        } finally {
          add.classList.remove('is-busy');
        }
        return;
      }

      if (!e.target.closest('[data-quick-panel]')) closeAll();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const panel = e.target.closest && e.target.closest('[data-quick-panel]');
      closeAll();
      if (panel) { const btn = $('[data-quick-open]', panel.closest('.card__media-wrap')); if (btn) btn.focus(); }
    });
  };

  /* ---------- Complete o look (página de produto) ---------- */
  const initLook = (productRoot) => {
    const look = $('[data-look]', productRoot);
    if (!look) return;
    let currentColor = null;
    const norm = (s) => String(s || '').trim().toLowerCase();

    const bindItem = (item) => {
      if (item.dataset.bound) return;
      item.dataset.bound = '1';
      const data = JSON.parse($('[data-look-json]', item).textContent);
      const selects = $$('[data-look-opt]', item);
      const btn = $('[data-look-add]', item);
      const err = $('[data-look-error]', item);
      const price = $('[data-look-price]', item);
      const img = $('[data-look-img]', item);

      const opts = () => {
        const out = data.variants[0].options.map(() => null);
        selects.forEach((s) => { out[+s.dataset.lookOpt] = s.value || null; });
        if (!selects.length) return data.variants[0].options;
        return out;
      };
      const find = (o) => data.variants.find((v) => v.options.every((x, i) => o[i] === x)) || null;

      const refresh = () => {
        const o = opts();
        // tamanhos esgotados na cor atual
        const sizeSel = selects.find((s) => +s.dataset.lookOpt === data.sizeIndex);
        if (sizeSel) {
          $$('option', sizeSel).forEach((op) => {
            if (!op.value) return;
            if (!op.dataset.label) op.dataset.label = op.textContent;
            const test = [...o];
            test[data.sizeIndex] = op.value;
            const v = find(test);
            const out = !v || !v.available;
            op.disabled = out;
            op.textContent = out ? `${op.dataset.label} · ${S.soldOutShort}` : op.dataset.label;
          });
          if (sizeSel.selectedOptions[0] && sizeSel.selectedOptions[0].disabled) sizeSel.value = '';
        }
        const v = find(opts());
        const ref = v || data.variants.find((x) => data.colorIndex < 0 || x.options[data.colorIndex] === o[data.colorIndex]) || data.variants[0];
        price.textContent = money(ref.price);
        if (img && ref.img) { img.src = ref.img; img.removeAttribute('srcset'); }
        btn.disabled = !!(v && !v.available);
      };

      const applyColor = (color) => {
        if (data.colorIndex < 0 || !color) return;
        const sel = selects.find((s) => +s.dataset.lookOpt === data.colorIndex);
        if (!sel) return;
        const match = $$('option', sel).find((op) => norm(op.value) === norm(color));
        const hasStock = match && data.variants.some((v) => v.available && norm(v.options[data.colorIndex]) === norm(color));
        if (match && hasStock && sel.value !== match.value) { sel.value = match.value; refresh(); }
      };
      item.applyColor = applyColor;

      item.addEventListener('change', (e) => {
        if (e.target.matches('[data-look-opt]')) { err.hidden = true; refresh(); }
      });

      btn.addEventListener('click', async () => {
        const v = find(opts());
        if (!v) {
          err.hidden = false;
          const sizeSel = selects.find((s) => +s.dataset.lookOpt === data.sizeIndex && !s.value);
          if (sizeSel) sizeSel.focus();
          return;
        }
        const label = btn.textContent;
        btn.disabled = true;
        btn.textContent = S.adding;
        try {
          await Cart.add([{ id: v.id, quantity: 1 }]);
          if (D.cartDrawer !== false) Cart.open();
          else window.location.href = D.routes.cart;
        } catch (e) {
          toast(e.message || S.cartError);
        } finally {
          btn.disabled = false;
          btn.textContent = label;
        }
      });

      refresh();
      applyColor(currentColor);
    };

    const bindAll = () => {
      const items = $$('[data-look-item]', look);
      items.forEach(bindItem);
      const hint = $('[data-look-hint]', look);
      if (hint) hint.hidden = items.length > 0;
      look.classList.toggle('is-empty', !items.length && !window.Shopify?.designMode);
    };

    productRoot.addEventListener('product:color', (e) => {
      currentColor = e.detail.color;
      $$('[data-look-item]', look).forEach((item) => item.applyColor && item.applyColor(currentColor));
    });

    bindAll();
    if (look.dataset.url) {
      fetch(look.dataset.url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) return;
          const fresh = htmlFrom(html).querySelector('[data-look-list]');
          const list = $('[data-look-list]', look);
          if (fresh && list && fresh.children.length) {
            list.innerHTML = fresh.innerHTML;
            bindAll();
          }
        })
        .catch(() => {});
    }
  };

  /* ---------- Zoom da galeria (página de produto) ---------- */
  const initZoom = (productRoot) => {
    const dialog = $('[data-zoom-dialog]', productRoot);
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const stage = $('[data-zoom-stage]', dialog);
    const img = $('[data-zoom-img]', dialog);
    let last = null;

    const setZoom = (on, e) => {
      stage.classList.toggle('is-zoomed', on);
      if (on && e) {
        // centraliza o ponto clicado
        const r = stage.getBoundingClientRect();
        const fx = (e.clientX - r.left) / r.width;
        const fy = (e.clientY - r.top) / r.height;
        requestAnimationFrame(() => {
          stage.scrollLeft = fx * (stage.scrollWidth - stage.clientWidth);
          stage.scrollTop = fy * (stage.scrollHeight - stage.clientHeight);
        });
      }
    };

    productRoot.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-zoom]');
      if (!btn) return;
      last = btn;
      img.src = btn.dataset.zoom;
      img.alt = btn.dataset.zoomAlt || '';
      setZoom(false);
      dialog.showModal();
      lockScroll(true);
    });
    stage.addEventListener('click', (e) => setZoom(!stage.classList.contains('is-zoomed'), e));
    stage.addEventListener('mousemove', (e) => {
      if (!stage.classList.contains('is-zoomed') || e.buttons) return;
      const r = stage.getBoundingClientRect();
      stage.scrollLeft = ((e.clientX - r.left) / r.width) * (stage.scrollWidth - stage.clientWidth);
      stage.scrollTop = ((e.clientY - r.top) / r.height) * (stage.scrollHeight - stage.clientHeight);
    });
    $('[data-zoom-close]', dialog).addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => {
      lockScroll(false);
      img.removeAttribute('src');
      if (last) last.focus({ preventScroll: true });
    });
  };

  /* ---------- Guia de medidas em janela lateral (página de produto) ---------- */
  let sgLastFocus = null;
  const closeSizeDrawer = () => {
    const overlay = $('[data-sg-overlay]');
    if (!overlay || !overlay.classList.contains('is-open')) return;
    overlay.classList.remove('is-open');
    lockScroll(false);
    if (sgLastFocus) sgLastFocus.focus({ preventScroll: true });
  };

  const initSizeDrawer = (productRoot) => {
    const overlay = $('[data-sg-overlay]', productRoot);
    if (!overlay) return;
    const body = $('[data-sg-body]', overlay);
    let loaded = false;

    const load = async (href) => {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('view', 'medidas');
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(res.status);
      const sg = htmlFrom(await res.text()).querySelector('[data-size-guide]');
      if (!sg) throw new Error('sem guia');
      sg.classList.add('sg--drawer');
      sg.dataset.gender = body.dataset.gender || sg.dataset.default;
      const title = sg.querySelector('.h-page');
      if (title) title.remove();
      // dentro da janela não há animação de entrada
      $$('[data-reveal]', sg).forEach((el) => el.removeAttribute('data-reveal'));
      if (sg.hasAttribute('data-reveal')) sg.removeAttribute('data-reveal');
      body.innerHTML = '';
      body.appendChild(sg);
      initSizeGuide(body);
      loaded = true;
    };

    productRoot.addEventListener('click', async (e) => {
      const link = e.target.closest('[data-size-guide-open]');
      if (!link) return;
      e.preventDefault();
      sgLastFocus = link;
      overlay.classList.add('is-open');
      lockScroll(true);
      setTimeout(() => $('[data-sg-drawer]', overlay).focus(), 50);
      if (!loaded) {
        try { await load(link.getAttribute('href')); } catch (err) { window.location.href = link.getAttribute('href'); }
      }
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.closest('[data-sg-close]')) closeSizeDrawer();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSizeDrawer(); });
  };

  /* ---------- Formulário de produto + bordado ---------- */
  const initProduct = (root) => {
    const dataEl = $('[data-product-json]', root);
    if (!dataEl) return;
    const data = JSON.parse(dataEl.textContent);
    const form = $('[data-product-form]', root);
    const addBtn = $('[data-add]', root);
    const sizeError = $('[data-size-error]', root);
    const embToggle = $('[data-emb-toggle]', root);
    const embPanel = $('[data-emb-panel]', root);
    const preview = $('[data-emb-preview]', root);
    const embNameErr = $('[data-emb-name-error]', root);

    const selected = () => data.options.map((_, i) => {
      const checked = $(`input[name="option-${i}"]:checked`, root);
      return checked ? checked.value : null;
    });

    const findVariant = (opts) => data.variants.find((v) => v.options.every((o, i) => opts[i] === o)) || null;

    const embOn = () => !!(embToggle && embToggle.checked);
    const embPrice = () => (embOn() ? (data.embroideryPrice || 0) : 0);

    // Galeria: no celular o trilho rola com deslize (scroll-snap); no desktop as fotos se sobrepõem
    const track = $('[data-track]', root);
    const slides = $$('[data-slide]', root);
    const dots = $$('[data-dot]', root);
    const counter = $('[data-count]', root);
    const isSwipe = () => !!track && track.scrollWidth > track.clientWidth + 1;

    const markActive = (slide) => {
      const index = slides.indexOf(slide);
      slides.forEach((s) => s.classList.toggle('is-active', s === slide));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      $$('[data-thumb]', root).forEach((t) => {
        const on = t.dataset.thumb === slide.dataset.mediaId;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });
      // a prévia do bordado se reposiciona para a foto ativa (listener abaixo de updatePreview)
      root.dispatchEvent(new CustomEvent('dutturi:slide'));
    };

    const showMedia = (mediaId) => {
      if (!mediaId) return;
      const slide = $(`[data-media-id="${mediaId}"]`, root);
      if (!slide) return;
      markActive(slide);
      if (isSwipe()) track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    if (track && slides.length > 1) {
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          if (!isSwipe()) return;
          const index = Math.round(track.scrollLeft / track.clientWidth);
          const slide = slides[Math.max(0, Math.min(slides.length - 1, index))];
          if (slide && !slide.classList.contains('is-active')) markActive(slide);
        });
      }, { passive: true });
      track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const current = slides.findIndex((s) => s.classList.contains('is-active'));
        const next = slides[current + (e.key === 'ArrowRight' ? 1 : -1)];
        if (next) { e.preventDefault(); showMedia(next.dataset.mediaId); }
      });
      // a foto inicial pode não ser a primeira (variante na URL)
      const initial = slides.find((s) => s.classList.contains('is-active'));
      if (initial && isSwipe()) track.scrollLeft = initial.offsetLeft;
    }

    // Barra de compra fixa (celular)
    const buybar = $('[data-buybar]', root);
    const barBtn = $('[data-buybar-add]', root);
    if (buybar && addBtn) {
      // aparece só depois que o botão principal passou para cima da tela
      // (checagem na rolagem: um IntersectionObserver perde saltos grandes de rolagem)
      let shown = false;
      let ticking = false;
      const check = () => {
        ticking = false;
        const show = addBtn.getBoundingClientRect().bottom < 0;
        if (show === shown) return;
        shown = show;
        buybar.classList.toggle('is-visible', show);
        buybar.inert = !show;
        document.body.classList.toggle('has-buybar', show);
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      check();
    }

    const update = () => {
      const opts = selected();
      const variant = findVariant(opts);

      // Nome da cor e rótulo do tamanho
      if (data.colorIndex > -1) {
        const colorName = $('[data-color-name]', root);
        if (colorName) colorName.textContent = opts[data.colorIndex] || '';
        const tier = $('[data-color-tier]', root);
        if (tier) {
          const isCore = tier.dataset.coreColors.includes('|' + (opts[data.colorIndex] || '').toLowerCase() + '|');
          tier.classList.toggle('is-limited', !isCore);
          tier.textContent = isCore ? tier.dataset.coreLabel : tier.dataset.limitedLabel;
        }
      }
      if (data.sizeIndex > -1) {
        const label = $('[data-size-label]', root);
        if (label) label.textContent = opts[data.sizeIndex] ? `${label.dataset.prefix} ${opts[data.sizeIndex]}` : label.dataset.empty;
        // disponibilidade dos tamanhos para a cor atual
        $$(`input[name="option-${data.sizeIndex}"]`, root).forEach((input) => {
          const test = [...opts];
          test[data.sizeIndex] = input.value;
          const v = findVariant(test);
          input.closest('.size-btn').classList.toggle('is-unavailable', !v || !v.available);
        });
      }

      // Imagem da cor
      const colorVariant = variant || data.variants.find((v) => data.colorIndex > -1 && v.options[data.colorIndex] === opts[data.colorIndex] && v.featured_media);
      if (colorVariant && colorVariant.featured_media) showMedia(colorVariant.featured_media);

      // Preço
      const base = variant ? variant.price : data.variants.find((v) => data.colorIndex < 0 || v.options[data.colorIndex] === opts[data.colorIndex]).price;
      const unit = base + embPrice();
      // o cálculo de frete por CEP lê o preço e o bordado daqui
      root.dataset.unitPrice = unit;
      root.dataset.emb = embOn() ? '1' : '';
      root.dispatchEvent(new CustomEvent('dutturi:price'));
      const pix = Math.round(unit * (100 - D.pixDiscount) / 100);
      const parcel = Math.floor(unit / (D.installments || 1));
      const set = (sel, txt) => { const el = $(sel, root); if (el) el.textContent = txt; };
      set('[data-price]', money(unit));
      set('[data-price-pix]', money(pix));
      set('[data-price-parcel]', money(parcel));
      const compare = $('[data-compare]', root);
      if (compare) {
        const cmp = variant && variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
        compare.hidden = !cmp;
        if (cmp) compare.textContent = money(cmp + embPrice());
      }

      // Botão
      const idInput = $('input[name="id"]', root);
      if (idInput) idInput.value = variant ? variant.id : '';
      // Estoque baixo (só variantes com estoque controlado) e "avise-me" em tamanho esgotado
      const lowEl = $('[data-low-stock]', root);
      if (lowEl) {
        const q = variant && variant.available && typeof variant.qty === 'number' ? variant.qty : null;
        const show = !!data.lowStock && q !== null && q > 0 && q <= data.lowStock;
        lowEl.hidden = !show;
        if (show) lowEl.textContent = q === 1 ? data.lowStockOne : data.lowStockText.replace('[n]', q);
      }
      const notify = $('[data-notify]', root);
      if (notify) {
        const out = !!(variant && !variant.available);
        notify.hidden = !out;
        if (out) {
          const sizeName = data.sizeIndex > -1 ? opts[data.sizeIndex] : '';
          $('[data-notify-size]', notify).textContent = sizeName;
          $('[data-notify-variant]', notify).value = opts.filter(Boolean).join(' / ');
        }
      }

      const soldOut = !!(variant && !variant.available);
      addBtn.disabled = soldOut;
      addBtn.textContent = soldOut ? S.soldOut : `${S.addToCart} — ${money(unit)}`;
      if (barBtn) {
        barBtn.disabled = soldOut;
        barBtn.textContent = soldOut ? S.soldOut : S.addShort;
        set('[data-buybar-price]', money(unit));
        const sizeLabel = $('[data-size-label]', root);
        set('[data-buybar-size]', sizeLabel ? sizeLabel.textContent.trim() : (opts.filter(Boolean).join(' / ')));
      }

      // "Complete o look" acompanha a cor escolhida
      if (data.colorIndex > -1 && opts[data.colorIndex]) {
        root.dispatchEvent(new CustomEvent('product:color', { detail: { color: opts[data.colorIndex] } }));
      }

      if (variant && data.updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    const updatePreview = () => {
      if (!preview) return;
      const name = $('[data-emb-name]', root);
      const reg = $('[data-emb-reg]', root);
      const font = $('input[name="emb-font"]:checked', root);
      const thread = $('input[name="emb-thread"]:checked', root);
      const outName = $('[data-emb-out-name]', preview);
      const outReg = $('[data-emb-out-reg]', preview);
      outName.textContent = (name.value.trim() || outName.dataset.default);
      outReg.textContent = (reg.value.trim() || outReg.dataset.default);
      outName.classList.toggle('is-block', font && font.value === 'block');
      const side = $('input[name="emb-side"]:checked', root);
      preview.classList.toggle('is-right', !!side && side.value === 'right');
      const hex = thread ? thread.dataset.hex : '#0A0F14';
      outName.style.color = hex;
      outReg.style.color = hex;
      preview.style.borderColor = hex === '#FFFFFF' ? 'rgba(255,255,255,.6)' : 'rgba(10,15,20,.4)';
      const threadName = $('[data-thread-name]', root);
      if (threadName && thread) threadName.textContent = thread.dataset.name;
      // Foto de frente calibrada: aproxima no peito e posiciona o bordado; sem calibração, posição fixa na 1ª foto
      const active = $('[data-slide].is-active', root);
      const aimg = active && $('img', active);
      $$('[data-slide] img', root).forEach((i) => { if (i !== aimg) i.style.transform = ''; });
      const cal = aimg && embCalib()[photoKey(aimg.currentSrc || aimg.src)];
      if (embOn() && cal && aimg.complete && aimg.naturalWidth) {
        setBox(preview, placeEmb($('.pdp__main', root), aimg, cal, side ? side.value : 'left'));
        preview.classList.add('is-placed');
        preview.hidden = false;
        fitEmbText(preview);
      } else {
        if (aimg) aimg.style.transform = '';
        preview.classList.remove('is-placed');
        preview.style.left = '';
        preview.style.top = '';
        preview.style.width = '';
        [outName, outReg].forEach((el) => { el.style.fontSize = ''; el.style.whiteSpace = ''; });
        preview.hidden = !embOn() || !(active && active.hasAttribute('data-first'));
      }
    };

    root.addEventListener('change', (e) => {
      if (e.target.name && e.target.name.startsWith('option-')) {
        if (sizeError && e.target.name === `option-${data.sizeIndex}`) sizeError.hidden = true;
        update();
      }
      if (e.target === embToggle) {
        embPanel.hidden = !embToggle.checked;
        update();
        updatePreview();
      }
      if (e.target.name === 'emb-font' || e.target.name === 'emb-thread' || e.target.name === 'emb-side') updatePreview();
    });
    root.addEventListener('input', (e) => {
      if (e.target.matches('[data-emb-name], [data-emb-reg]')) {
        if (embNameErr && e.target.matches('[data-emb-name]')) embNameErr.hidden = true;
        updatePreview();
      }
    });
    $$('[data-thumb]', root).forEach((t) => t.addEventListener('click', () => {
      showMedia(t.dataset.thumb);
      updatePreview();
    }));
    root.addEventListener('dutturi:slide', () => { if (preview) updatePreview(); });
    $$('[data-slide] img', root).forEach((i) => i.addEventListener('load', () => { if (embOn()) updatePreview(); }));
    window.addEventListener('resize', debounce(() => { if (embOn()) updatePreview(); }, 150));

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const opts = selected();
      if (data.sizeIndex > -1 && !opts[data.sizeIndex]) {
        sizeError.hidden = false;
        sizeError.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        return;
      }
      const variant = findVariant(opts);
      if (!variant) { toast(S.unavailable); return; }

      const items = [];
      const piece = { id: variant.id, quantity: 1, properties: {} };
      if (embOn()) {
        const name = $('[data-emb-name]', root).value.trim();
        if (!name) {
          embNameErr.hidden = false;
          $('[data-emb-name]', root).focus();
          return;
        }
        const reg = $('[data-emb-reg]', root).value.trim();
        const font = $('input[name="emb-font"]:checked', root);
        const thread = $('input[name="emb-thread"]:checked', root);
        const bid = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        const sideEl = $('input[name="emb-side"]:checked', root);
        piece.properties = {
          'Bordado – Nome': name,
          'Bordado – Registro': reg,
          'Bordado – Linha': thread ? thread.dataset.name : '',
          'Bordado – Fonte': font ? font.dataset.label : '',
          ...(sideEl ? { 'Bordado – Lado': sideEl.dataset.label } : {}),
          _bordado_id: bid
        };
        items.push(piece);
        if (D.embroidery && D.embroidery.variantId) {
          items.push({ id: D.embroidery.variantId, quantity: 1, properties: { _bordado_id: bid, _peca: data.title } });
        } else {
          console.warn('[Dutturi] Configure o produto oculto de bordado em Configurações do tema › Regras da loja.');
        }
      } else {
        items.push(piece);
      }

      const label = addBtn.textContent;
      const barLabel = barBtn ? barBtn.textContent : '';
      addBtn.disabled = true;
      addBtn.textContent = S.adding;
      if (barBtn) { barBtn.disabled = true; barBtn.textContent = S.adding; }
      try {
        await Cart.add(items);
        if (D.cartDrawer !== false) Cart.open();
        else window.location.href = D.routes.cart;
      } catch (err) {
        toast(err.message || S.cartError);
      } finally {
        addBtn.disabled = false;
        addBtn.textContent = label;
        if (barBtn) { barBtn.disabled = false; barBtn.textContent = barLabel; }
      }
    });

    initLook(root);
    initSizeDrawer(root);
    initZoom(root);

    // Escolha vinda do guia ("Usar este tamanho")
    document.addEventListener('sizeguide:pick', (e) => {
      if (data.sizeIndex < 0 || !root.isConnected) return;
      const wanted = String(e.detail.size || '').trim().toLowerCase();
      const input = $$(`input[name="option-${data.sizeIndex}"]`, root).find((i) => i.value.trim().toLowerCase() === wanted);
      if (!input) { toast(S.sizeNotFound.replace('[tamanho]', e.detail.size)); return; }
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      closeSizeDrawer();
      addBtn.focus({ preventScroll: true });
      input.closest('fieldset').scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    });

    // volta do formulário "avise-me"
    if (/[?&]contact_posted=true/.test(window.location.search)) {
      const ok = $('.notify__ok', root);
      if (ok) ok.scrollIntoView({ block: 'center' });
    }

    // rolagem até as avaliações
    $$('[data-scroll-reviews]', root).forEach((a) => a.addEventListener('click', (e) => {
      const target = document.querySelector('[data-reviews]');
      if (!target) return;
      e.preventDefault();
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 90, behavior: reduceMotion ? 'auto' : 'smooth' });
    }));

    update();
    updatePreview();
  };

  /* ---------- Filtros da coleção / busca ---------- */
  const initFacets = (root) => {
    const sectionId = root.dataset.sectionId;
    const getForm = () => $('[data-facets-form]', root);

    const render = async (params) => {
      const results = $('[data-plp-results]', root);
      if (results) results.classList.add('is-loading');
      const url = `${window.location.pathname}?${params}`;
      try {
        const res = await fetch(`${url}${params ? '&' : ''}section_id=${sectionId}`);
        const doc = htmlFrom(await res.text());
        ['[data-plp-results]', '[data-facets-inner]', '[data-plp-count]'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = $(sel, root);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        window.history.replaceState({}, '', url);
        Wishlist.paint(root);
        initReveal(root);
      } catch (err) {
        window.location.href = url;
      } finally {
        const r = $('[data-plp-results]', root);
        if (r) r.classList.remove('is-loading');
      }
    };

    const paramsFromForm = () => {
      const form = getForm();
      const fd = new FormData(form);
      const bucket = $('input[name="price_bucket"]:checked', form);
      fd.delete('price_bucket');
      if (bucket) {
        if (bucket.dataset.min) fd.set('filter.v.price.gte', bucket.dataset.min);
        if (bucket.dataset.max) fd.set('filter.v.price.lte', bucket.dataset.max);
      }
      const sort = $('[data-sort]', root);
      if (sort) fd.set('sort_by', sort.value);
      const q = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') q.append(k, v);
      return q.toString();
    };

    root.addEventListener('change', (e) => {
      if (e.target.closest('[data-facets-form]') || e.target.matches('[data-sort]')) render(paramsFromForm());
    });
    root.addEventListener('click', (e) => {
      const clear = e.target.closest('[data-facets-clear]');
      if (clear) {
        e.preventDefault();
        const sort = $('[data-sort]', root);
        render(sort && sort.value ? `sort_by=${sort.value}` : '');
      }
      const opener = e.target.closest('[data-facets-open]');
      const facets = $('[data-facets]', root);
      const overlay = $('[data-facets-overlay]', root);
      if (opener && facets) { facets.classList.add('is-open'); overlay.hidden = false; lockScroll(true); }
      if ((e.target.closest('[data-facets-close]') || e.target === overlay) && facets) {
        facets.classList.remove('is-open'); overlay.hidden = true; lockScroll(false);
      }
    });
  };

  /* ---------- Abas (Mais desejados, guia de medidas, conta) ---------- */
  const initTabs = (root = document) => {
    $$('[data-tabs]', root).forEach((group) => {
      const btns = $$('[data-tab]', group);
      btns.forEach((btn) => btn.addEventListener('click', () => {
        btns.forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          const panel = document.getElementById(b.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        group.dispatchEvent(new CustomEvent('tabs:change', { detail: { value: btn.dataset.tab } }));
        initReveal(group);
      }));
      const fromHash = window.location.hash && btns.find((b) => `#${b.dataset.tab}` === window.location.hash);
      if (fromHash) fromHash.click();
    });
  };

  /* ---------- Selects com valor padrão (país) ---------- */
  const initSelectDefaults = (root = document) => {
    $$('select[data-default]', root).forEach((sel) => {
      const v = sel.dataset.default;
      if (v && Array.from(sel.options).some((o) => o.value === v)) sel.value = v;
    });
  };

  /* ---------- Hero técnico: ficha + hotspots ---------- */
  const initSpecs = (root = document) => {
    $$('[data-specs]', root).forEach((group) => {
      const items = $$('[data-spec]', group);
      const activate = (i) => items.forEach((el) => {
        const on = el.dataset.spec === i;
        el.classList.toggle('is-active', on);
        if (el.tagName === 'BUTTON') el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      items.forEach((el) => {
        el.addEventListener('mouseenter', () => activate(el.dataset.spec));
        el.addEventListener('focus', () => activate(el.dataset.spec));
        el.addEventListener('click', () => activate(el.dataset.spec));
      });
    });
  };

  /* ---------- Prévia do bordado na home ---------- */
  // Reduz a fonte até o texto caber na largura da área de bordado. Abaixo de 55% do tamanho original,
  // em vez de ficar ilegível, o texto quebra em duas linhas.
  const fitText = (el) => {
    const box = el.parentElement;
    if (!box || !box.clientWidth) return;
    el.style.fontSize = '';
    el.style.whiteSpace = '';
    const base = parseFloat(getComputedStyle(el).fontSize);
    const cs = getComputedStyle(box);
    const avail = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    let size = base;
    while (el.scrollWidth > avail && size > base * 0.55) {
      size -= 1;
      el.style.fontSize = size + 'px';
    }
    if (el.scrollWidth > avail) el.style.whiteSpace = 'normal';
  };

  const initEmbLive = (root = document) => {
    $$('[data-emb-scope]', root).forEach((scope) => {
      const outs = $$('[data-emb-out]', scope);
      const fitAll = () => outs.forEach(fitText);
      $$('[data-emb-live]', scope).forEach((input) => {
        const out = $(`[data-emb-out="${input.dataset.embLive}"]`, scope);
        if (!out) return;
        const paint = () => { out.textContent = input.value.trim() || out.dataset.default; fitText(out); };
        input.addEventListener('input', paint);
        paint();
      });
      // Fonte do bordado (Cursiva ou Bastão), igual à da página do produto
      const nameOut = $('[data-emb-out="name"]', scope);
      $$('[data-emb-font]', scope).forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!nameOut || !radio.checked) return;
          nameOut.classList.toggle('is-block', radio.value === 'block');
          fitText(nameOut);
        });
      });
      fitAll();
      // a fonte script carrega depois e muda a largura; o tamanho da tela também
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
      window.addEventListener('resize', fitAll, { passive: true });
    });
  };

  /* ---------- Guia de medidas ---------- */
  const initSizeGuide = (root = document) => {
    $$('[data-size-guide]', root).forEach((sg) => {
      const data = JSON.parse($('[data-size-json]', sg).textContent);
      const strings = data.strings;
      let gender = sg.dataset.gender || sg.dataset.default || 'fem';
      const params = new URLSearchParams(window.location.search);
      if (!sg.dataset.gender && (params.get('g') === 'masc' || params.get('g') === 'fem')) gender = params.get('g');
      let mode = 'tape';
      const inDrawer = !!sg.closest('[data-sg-drawer]');

      const num = (el) => (el ? parseFloat((el.value || '').replace(',', '.')) : NaN);
      const rec = $('[data-rec]', sg);
      const noRec = $('[data-no-rec]', sg);
      const use = $('[data-rec-use]', sg);

      // Fita métrica: primeira linha que comporta tórax e quadril
      const byTape = (rows) => {
        const mt = num($('[data-measure="t"]', sg));
        const mq = num($('[data-measure="q"]', sg));
        if (!(mt > 0) && !(mq > 0)) return '';
        const m = rows.find((r) => (!(mt > 0) || mt <= r.t[1]) && (!(mq > 0) || mq <= r.q[1]));
        return m ? m.s : (rows.length ? rows[rows.length - 1].s + '+' : '');
      };

      // Altura e peso: linha pelo peso; se a altura passar da faixa dessa linha, sobe um tamanho
      const byHeightWeight = (rows) => {
        const h = num($('[data-measure="h"]', sg));
        const w = num($('[data-measure="w"]', sg));
        if (!(w > 0)) return '';
        const list = rows.filter((r) => r.w);
        if (!list.length) return '';
        let i = list.findIndex((r) => w <= r.w[1]);
        if (i === -1) return list[list.length - 1].s + '+';
        if (h > 0 && list[i].h && list[i].h[1] > 0 && h > list[i].h[1] + 2 && i < list.length - 1) i += 1;
        return list[i].s;
      };

      const paint = () => {
        $$('[data-gender-tab]', sg).forEach((b) => {
          const on = b.dataset.genderTab === gender;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        $$('[data-sg-mode]', sg).forEach((b) => {
          const on = b.dataset.sgMode === mode;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        $$('[data-sg-panel]', sg).forEach((p) => { p.hidden = p.dataset.sgPanel !== mode; });
        $$('[data-gender-label]', sg).forEach((el) => { el.textContent = strings[gender]; });
        $$('[data-rows]', sg).forEach((tb) => { tb.hidden = tb.dataset.rows !== gender; });

        const rows = data[gender] || [];
        const size = mode === 'hw' ? byHeightWeight(rows) : byTape(rows);
        $$(`[data-rows="${gender}"] tr`, sg).forEach((tr) => tr.classList.toggle('is-rec', !!size && tr.dataset.size === size));
        rec.hidden = !size;
        noRec.hidden = !!size;
        noRec.textContent = (mode === 'hw' ? noRec.dataset.hintHw : noRec.dataset.hintTape) || noRec.textContent;
        if (size) {
          const custom = size.endsWith('+');
          const tpl = mode === 'hw' && strings.recHw ? strings.recHw : strings.rec;
          $('[data-rec-size]', rec).textContent = size;
          $('[data-rec-text]', rec).textContent = custom ? strings.custom : tpl.replace('[tamanho]', size);
          if (use) {
            use.hidden = !inDrawer || custom;
            use.dataset.size = size;
          }
        }
      };

      $$('[data-gender-tab]', sg).forEach((b) => b.addEventListener('click', () => { gender = b.dataset.genderTab; paint(); }));
      $$('[data-sg-mode]', sg).forEach((b) => b.addEventListener('click', () => { mode = b.dataset.sgMode; paint(); }));
      $$('[data-measure]', sg).forEach((i) => i.addEventListener('input', paint));
      if (use) use.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('sizeguide:pick', { detail: { size: use.dataset.size } }));
      });
      paint();
    });
  };

  /* ---------- Validação de formulários (B2B, contato, newsletter, conta) ---------- */
  const initValidation = (root = document) => {
    $$('form[data-validate]', root).forEach((form) => {
      form.setAttribute('novalidate', '');
      form.addEventListener('submit', (e) => {
        let firstBad = null;
        $$('[data-rule]', form).forEach((field) => {
          const rules = field.dataset.rule.split(' ');
          const v = field.value || '';
          let msg = '';
          if (rules.includes('required') && v.trim().length < (parseInt(field.dataset.min, 10) || 1)) msg = field.dataset.msg || S.required;
          if (!msg && rules.includes('email') && !isEmail(v)) msg = field.dataset.msg || S.emailInvalid;
          if (!msg && rules.includes('phone') && digits(v).length < 10) msg = field.dataset.msg || S.phoneInvalid;
          const err = form.querySelector(`[data-error-for="${field.id}"]`);
          field.setAttribute('aria-invalid', msg ? 'true' : 'false');
          if (err) { err.textContent = msg; err.hidden = !msg; }
          if (msg && !firstBad) firstBad = field;
        });
        if (firstBad) { e.preventDefault(); firstBad.focus(); }
      });
      form.addEventListener('input', (e) => {
        if (!e.target.dataset.rule) return;
        const err = form.querySelector(`[data-error-for="${e.target.id}"]`);
        if (err) err.hidden = true;
        e.target.setAttribute('aria-invalid', 'false');
      });
    });

    $$('form[data-validate-email]', root).forEach((form) => {
      form.setAttribute('novalidate', '');
      form.addEventListener('submit', (e) => {
        const input = $('input[type="email"]', form);
        const err = $('[data-email-error]', form);
        if (input && !isEmail(input.value)) {
          e.preventDefault();
          if (err) err.textContent = S.emailInvalid;
          input.focus();
        }
      });
    });
  };

  /* ---------- Página de favoritos ---------- */
  const initWishlistPage = (root = document) => {
    $$('[data-wishlist-page]', root).forEach(async (page) => {
      const grid = $('[data-wishlist-grid]', page);
      const empty = $('[data-wishlist-empty]', page);
      const render = async () => {
        const list = Wishlist.read();
        empty.hidden = list.length > 0;
        if (!list.length) { grid.innerHTML = ''; return; }
        const cards = await Promise.all(list.map(async (handle) => {
          try {
            const res = await fetch(`${D.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=product-card-ajax`);
            if (!res.ok) return '';
            const doc = htmlFrom(await res.text());
            const card = doc.querySelector('[data-product-card]');
            return card ? `<li>${card.outerHTML}</li>` : '';
          } catch (e) { return ''; }
        }));
        grid.innerHTML = cards.join('');
        Wishlist.paint(grid);
      };
      document.addEventListener('wishlist:change', render);
      render();
    });
  };

  /* ---------- Recomendações (produto) ---------- */
  const initRecommendations = (root = document) => {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (!el.dataset.url) return;
      try {
        const res = await fetch(el.dataset.url);
        const doc = htmlFrom(await res.text());
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          el.closest('[data-recommendations-wrap]').hidden = !$('[data-product-card]', el);
          Wishlist.paint(el);
          initReveal(el);
        }
      } catch (e) { /* silencioso */ }
    });
  };


  /* ---------- Frete por CEP (página do produto) ---------- */
  // Tarifas vêm das configurações do tema (espelho das tarifas do admin); o checkout confirma o valor final.
  const SHIP_KEY = 'dutturi:cep';
  const initShipCalc = (root = document) => {
    $$('[data-ship-calc]', root).forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      const cfg = JSON.parse($('[data-ship-config]', box).textContent);
      const input = $('[data-ship-cep]', box);
      const err = $('[data-ship-error]', box);
      const out = $('[data-ship-result]', box);
      const product = box.closest('[data-product-root]');
      let lastCep = '';

      const fmtCep = (v) => {
        const d = v.replace(/\D/g, '').slice(0, 8);
        return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
      };
      const inPickup = (digits) => {
        const n = parseInt(digits, 10);
        return (cfg.pickup.ranges || '').split(',').some((r) => {
          const [a, b] = r.split('-').map((x) => parseInt(x.replace(/\D/g, '').padEnd(8, '0'), 10));
          return a && b && n >= a && n <= b;
        });
      };
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

      const render = async (digits) => {
        let cartTotal = 0;
        try { cartTotal = (await (await fetch(`${D.routes.root}cart.js`)).json()).total_price || 0; } catch (e) { /* sem carrinho */ }
        const unit = product && product.dataset.unitPrice ? Number(product.dataset.unitPrice) : cfg.price;
        const embroidered = !!((product && product.dataset.emb) || cfg.cartEmb);
        const total = cartTotal + unit;
        const free = cfg.threshold > 0 && total >= cfg.threshold;
        const rows = cfg.rates.filter((r) => r.name).map((r) => {
          const isFree = r.free && free;
          return `<li><span><strong>${esc(r.name)}</strong><small>${esc(r.days)}</small></span><span>${isFree ? esc(cfg.freeLabel) : money(r.price)}</span></li>`;
        });
        if (cfg.pickup.name && inPickup(digits)) {
          rows.push(`<li><span><strong>${esc(cfg.pickup.name)}</strong><small>${esc(cfg.pickup.days)}</small></span><span>${esc(cfg.freeLabel)}</span></li>`);
        }
        let hint = '';
        if (!free && cfg.threshold > 0) hint = `<p class="ship-calc__hint">Faltam ${money(cfg.threshold - total)} para o frete grátis no ${esc(cfg.rates[0].name)}.</p>`;
        const prep = embroidered ? cfg.embroidered : cfg.prep;
        out.innerHTML = `<p class="ship-calc__cep">CEP ${fmtCep(digits)}</p><ul class="ship-calc__list">${rows.join('')}</ul>${hint}<p class="ship-calc__note">${esc(prep)} ${esc(cfg.note)}</p>`;
        out.hidden = false;
      };

      const run = () => {
        const digits = input.value.replace(/\D/g, '');
        if (digits.length !== 8) { err.hidden = false; out.hidden = true; input.focus(); return; }
        err.hidden = true;
        lastCep = digits;
        try { localStorage.setItem(SHIP_KEY, digits); } catch (e) { /* sem storage */ }
        render(digits);
      };

      input.addEventListener('input', () => { input.value = fmtCep(input.value); err.hidden = true; });
      $('[data-ship-form]', box).addEventListener('submit', (e) => { e.preventDefault(); run(); });
      // troca de variante ou de bordado refaz a conta, se já houver CEP
      if (product) product.addEventListener('dutturi:price', () => { if (lastCep) render(lastCep); });
      try {
        const saved = localStorage.getItem(SHIP_KEY);
        if (saved && saved.length === 8) {
          input.value = fmtCep(saved);
          lastCep = saved;
          render(saved);
          const details = $('[data-ship-details]', box);
          if (details) details.open = true;
        }
      } catch (e) { /* sem storage */ }
    });
  };


  /* ---------- Bordado na foto da peça (calibração) ---------- */
  // Calibração por foto de frente (snippet emb-calib): [centro x, peito y, ombros] em % da foto.
  const EMB_KEY = 'dutturi:bordado';
  const readDraft = () => { try { return JSON.parse(localStorage.getItem(EMB_KEY) || 'null'); } catch (e) { return null; } };
  let calibCache = null;
  const embCalib = () => {
    if (calibCache) return calibCache;
    const el = $('[data-emb-calib]');
    try { calibCache = el ? JSON.parse(el.textContent) : {}; } catch (e) { calibCache = {}; }
    return calibCache;
  };
  const photoKey = (src) => ((src || '').split('?')[0].split('/').pop() || '').replace(/\.[a-z]+$/i, '').split('_')[0];

  // Aproxima a foto (object-fit: cover, recorte a 20% do topo) no peito e devolve a caixa do bordado, em px do quadro.
  // drop: o bordado fica um pouco abaixo da linha do peito, livre do logo das peças
  // width: bordado real (~9 cm) sobre ~45 cm de ombro a ombro; win: largura da janela, em larguras de ombro
  const EMB = { win: 1.4, side: 0.26, width: 0.2, chestAt: 0.42, drop: 0.03 };
  const placeEmb = (frame, img, cal, side, zoom = true) => {
    const W = frame.clientWidth;
    const H = frame.clientHeight;
    const ar = (img.naturalHeight && img.naturalWidth) ? img.naturalHeight / img.naturalWidth : 1.5;
    let dw; let dh; let ox; let oy;
    if (W * ar >= H) { dw = W; dh = W * ar; ox = 0; oy = -(dh - H) * 0.2; } else { dh = H; dw = H / ar; oy = 0; ox = -(dw - W) / 2; }
    const [cx, cy, sh] = cal.map((n) => n / 100);
    let k = 1; let tx = 0; let ty = 0;
    if (zoom) {
      k = Math.max(1, W / (EMB.win * sh * dw));
      tx = W / 2 - k * (ox + cx * dw);
      ty = H * EMB.chestAt - k * (oy + cy * dh);
      // a foto continua cobrindo o quadro
      tx = Math.min(-k * ox, Math.max(W - k * (ox + dw), tx));
      ty = Math.min(-k * oy, Math.max(H - k * (oy + dh), ty));
    }
    img.style.transformOrigin = '0 0';
    img.style.transform = zoom ? `translate(${tx}px, ${ty}px) scale(${k})` : '';
    // ponto do bordado: calibrado (logo abaixo do logo da peça) ou estimado pelo peito; o lado direito é o espelho
    const ex = cal.length > 4 ? cal[3] / 100 : cx + EMB.side * sh;
    const ey = cal.length > 4 ? (cal[4] / 100) * dh : cy * dh + EMB.drop * sh * dw;
    const px = side === 'right' ? 2 * cx - ex : ex;
    const w = k * EMB.width * sh * dw;
    return { left: tx + k * (ox + px * dw) - w / 2, top: ty + k * (oy + ey), width: w };
  };
  const setBox = (el, box) => {
    el.style.left = `${box.left}px`;
    el.style.top = `${box.top}px`;
    el.style.width = `${box.width}px`;
  };
  // Letra em escala com a peça: o nome ocupa no máximo a largura do bordado, com altura de letra de bordado
  // (cursiva até ~16% da largura, bastão até ~9%); o registro sai menor, na mesma largura.
  const fitEmbText = (box) => {
    const w = parseFloat(box.style.width) || box.clientWidth;
    if (!w) return;
    const fit = (el, cap) => {
      if (!el || el.hidden || !el.textContent.trim()) return 0;
      el.style.whiteSpace = 'nowrap';
      el.style.fontSize = '100px';
      const tw = el.scrollWidth || 1;
      const fs = Math.max(4, Math.min(100 * w / tw, cap));
      el.style.fontSize = `${fs}px`;
      return fs;
    };
    const [nameEl, regEl] = box.children;
    const block = nameEl && nameEl.classList.contains('is-block');
    const fs = fit(nameEl, w * (block ? 0.09 : 0.16));
    fit(regEl, Math.max(4, (block ? fs * 0.8 : fs * 0.42)));
  };

  /* ---------- Simulador de bordado (página própria) ---------- */
  const initEmbSimulator = (root = document) => {
    $$('[data-sim]', root).forEach((sim) => {
      if (sim.dataset.ready) return;
      sim.dataset.ready = '1';
      const stage = $('[data-sim-stage]', sim);
      const img = $('[data-sim-img]', sim);
      const text = $('[data-sim-text]', sim);
      const caption = $('[data-sim-caption]', sim);
      const colorsBox = $('[data-sim-colors]', sim);
      const name = $('[data-sim-name]', sim);
      const num = $('[data-sim-regnum]', sim);
      const outName = $('[data-sim-out-name]', sim);
      const outReg = $('[data-sim-out-reg]', sim);
      const err = $('[data-sim-error]', sim);
      const checked = (n) => $(`input[name="${n}"]:checked`, sim);
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const products = {};
      $$('[data-sim-product]', sim).forEach((el) => {
        try { const p = JSON.parse(el.textContent); if (p.colors.length) products[p.h] = p; } catch (e) { /* ignora */ }
      });
      const models = $$('.sim__model', sim).filter((m) => products[$('input', m).value]);
      $$('.sim__model', sim).forEach((m) => { if (!models.includes(m)) m.remove(); });

      const current = () => {
        const m = checked('sim-model');
        const p = m && products[m.value];
        const c = checked('sim-color');
        const color = p && (p.colors.find((x) => x.n === (c && c.value)) || p.colors[0]);
        return { p, color };
      };

      const state = () => {
        const type = checked('sim-reg');
        const regType = type ? type.value : '';
        const regNum = num.value.trim();
        // CRM + "CE 12345" vira "CRM-CE 12345", como no registro profissional
        let reg = '';
        if (regType) reg = !regNum ? regType : /^[A-Za-z]{2}\b/.test(regNum) ? `${regType}-${regNum.toUpperCase()}` : `${regType} ${regNum}`;
        const thread = checked('sim-thread');
        const font = checked('sim-font');
        const side = checked('sim-side');
        const { p, color } = current();
        return {
          name: name.value.trim(),
          regType,
          regNum,
          reg,
          font: font ? font.value : 'script',
          fontLabel: font ? font.dataset.label : '',
          thread: thread ? thread.dataset.name : '',
          hex: thread ? thread.dataset.hex : '#0A0F14',
          side: side ? side.value : 'left',
          product: p ? p.h : '',
          color: color ? color.n : '',
          variant: color ? color.v : null
        };
      };

      const place = () => {
        const { color } = current();
        if (!color || !img.complete || !img.naturalWidth) return;
        const cal = embCalib()[color.key] || [50, 33, 25];
        const side = (checked('sim-side') || {}).value;
        autoThread(cal, side);
        setBox(text, placeEmb(stage, img, cal, side));
        text.hidden = false;
        fitEmbText(text);
      };

      const paint = () => {
        const st = state();
        outName.textContent = st.name || outName.dataset.default;
        outReg.textContent = st.reg || '';
        outReg.hidden = !outReg.textContent;
        outName.classList.toggle('is-block', st.font === 'block');
        outName.style.color = st.hex;
        outReg.style.color = st.hex;
        num.disabled = !st.regType;
        const tn = $('[data-sim-thread-name]', sim);
        if (tn) tn.textContent = st.thread;
        requestAnimationFrame(place);
      };

      const updateLinks = () => {
        const { p, color } = current();
        if (!p) return;
        const base = color ? `${p.url}?variant=${color.v}` : p.url;
        $('[data-sim-buy]', sim).href = `${base}${color ? '&' : '?'}bordado=1`;
        $('[data-sim-view]', sim).href = base;
      };

      // até a cliente escolher a linha, ela contrasta com a peça: branca em tecido escuro, a primeira (Midnight) no claro.
      // Mede o tecido na própria foto, no ponto do bordado (a cor cadastrada nem sempre bate com o tom da foto).
      let threadTouched = !!(readDraft() || {}).thread;
      const fabricDark = (cal, side) => {
        try {
          const cv = document.createElement('canvas');
          cv.width = 24; cv.height = 24;
          const ctx = cv.getContext('2d', { willReadFrequently: true });
          const [cx, cy, sh] = cal.map((n) => n / 100);
          const ex = cal.length > 4 ? cal[3] / 100 : cx + EMB.side * sh;
          const nw = img.naturalWidth; const nh = img.naturalHeight; const r = sh * nw * 0.08;
          const ey = cal.length > 4 ? cal[4] / 100 : cy + EMB.drop * sh * nw / nh;
          const px = side === 'right' ? 2 * cx - ex : ex;
          ctx.drawImage(img, px * nw - r, ey * nh - r, r * 2, r * 2, 0, 0, 24, 24);
          const d = ctx.getImageData(0, 0, 24, 24).data;
          let sum = 0;
          for (let i = 0; i < d.length; i += 4) sum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
          return sum / (d.length / 4) / 255 < 0.5;
        } catch (e) { return null; }
      };
      const autoThread = (cal, side) => {
        if (threadTouched) return;
        const dark = fabricDark(cal, side);
        if (dark === null) return;
        const threads = $$('input[name="sim-thread"]', sim);
        const white = threads.find((i) => (i.dataset.hex || '').toUpperCase() === '#FFFFFF');
        const target = dark && white ? white : threads[0];
        if (target && !target.checked) { target.checked = true; paint(); }
      };

      const showPhoto = () => {
        const { p, color } = current();
        if (!color) return;
        text.hidden = true;
        img.style.transform = '';
        img.hidden = false;
        if (img.getAttribute('src') !== color.img) img.src = color.img;
        img.alt = `${p.t} ${color.n}`;
        caption.textContent = `${p.t} · ${color.n}`;
        if (img.complete) paint();
        updateLinks();
      };

      const renderColors = (keep) => {
        const { p } = current();
        if (!p) return;
        const prev = keep || (checked('sim-color') || {}).value;
        const has = p.colors.some((c) => c.n === prev);
        colorsBox.innerHTML = p.colors.map((c, i) => `
          <label class="sim__color" title="${esc(c.n)}">
            <input type="radio" name="sim-color" value="${esc(c.n)}"${(has ? c.n === prev : i === 0) ? ' checked' : ''}>
            <span class="sim__color-dot" style="background:${esc(c.hex || '#ccc')}"></span>
            <span class="sim__color-name">${esc(c.n)}</span>
          </label>`).join('');
        showPhoto();
      };

      const showModel = () => {
        const cat = (checked('sim-cat') || {}).value;
        models.forEach((m) => { m.hidden = m.dataset.cat !== cat; });
        const m = checked('sim-model');
        if (!m || m.closest('.sim__model').hidden) {
          const first = models.find((x) => !x.hidden);
          if (first) $('input', first).checked = true;
        }
        renderColors();
      };

      img.addEventListener('load', paint);

      // retoma uma simulação anterior
      const draft = readDraft();
      const pick = (n, test) => { const el = $$(`input[name="${n}"]`, sim).find(test); if (el) el.checked = true; return el; };
      if (draft) {
        name.value = draft.name || '';
        num.value = draft.regNum || '';
        pick('sim-reg', (i) => i.value === (draft.regType || ''));
        pick('sim-font', (i) => i.value === draft.font);
        pick('sim-thread', (i) => i.dataset.name === draft.thread);
        pick('sim-side', (i) => i.value === draft.side);
        const m = draft.product && pick('sim-model', (i) => i.value === draft.product);
        if (m) pick('sim-cat', (i) => i.value === m.closest('.sim__model').dataset.cat);
      }
      showModel();
      if (draft && draft.color) renderColors(draft.color);

      sim.addEventListener('input', (e) => { if (e.target === name) err.hidden = true; paint(); });
      sim.addEventListener('change', (e) => {
        if (e.target.name === 'sim-cat') { showModel(); return; }
        if (e.target.name === 'sim-model') { renderColors(); return; }
        if (e.target.name === 'sim-color') { showPhoto(); return; }
        if (e.target.name === 'sim-thread') threadTouched = true;
        paint();
      });
      $$('[data-sim-go]', sim).forEach((btn) => btn.addEventListener('click', (e) => {
        const st = state();
        if (btn.hasAttribute('data-sim-buy') && !st.name) { e.preventDefault(); err.hidden = false; name.focus(); return; }
        if (st.name) { try { localStorage.setItem(EMB_KEY, JSON.stringify({ ...st, ts: Date.now() })); } catch (x) { /* sem storage */ } }
      }));
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(paint);
      window.addEventListener('resize', debounce(paint, 150));
    });

    // Página do produto: aplica a simulação salva (automático quando vem do simulador com ?bordado=1)
    $$('[data-product-root]', root).forEach((product) => {
      const offer = $('[data-emb-draft]', product);
      const toggle = $('[data-emb-toggle]', product);
      const draft = readDraft();
      if (!offer || !toggle || !draft || !draft.name) return;
      const apply = () => {
        if (!toggle.checked) { toggle.checked = true; toggle.dispatchEvent(new Event('change', { bubbles: true })); }
        const nameEl = $('[data-emb-name]', product);
        const regEl = $('[data-emb-reg]', product);
        if (nameEl) nameEl.value = draft.name.slice(0, nameEl.maxLength > 0 ? nameEl.maxLength : 99);
        if (regEl) regEl.value = (draft.reg || '').slice(0, regEl.maxLength > 0 ? regEl.maxLength : 99);
        const pickP = (n, test) => {
          const el = $$(`input[name="${n}"]`, product).find(test);
          if (el) { el.checked = true; el.dispatchEvent(new Event('change', { bubbles: true })); }
        };
        pickP('emb-font', (i) => i.value === draft.font);
        pickP('emb-thread', (i) => i.dataset.name === draft.thread);
        pickP('emb-side', (i) => i.value === draft.side);
        if (nameEl) nameEl.dispatchEvent(new Event('input', { bubbles: true }));
        offer.hidden = true;
      };
      if (new URLSearchParams(window.location.search).get('bordado') === '1') { apply(); return; }
      $('[data-emb-draft-text]', offer).textContent = [draft.name, draft.reg].filter(Boolean).join(' · ');
      offer.hidden = false;
      $('[data-emb-draft-apply]', offer).addEventListener('click', apply);
      $('[data-emb-draft-dismiss]', offer).addEventListener('click', () => {
        offer.hidden = true;
        try { localStorage.removeItem(EMB_KEY); } catch (e) { /* sem storage */ }
      });
    });
  };

  /* ---------- Boot ---------- */
  const initSection = (root = document) => {
    initReveal(root);
    initTabs(root);
    initSelectDefaults(root);
    initSpecs(root);
    initEmbLive(root);
    initSizeGuide(root);
    initValidation(root);
    initWishlistPage(root);
    initRecommendations(root);
    $$('[data-product-root]', root).forEach(initProduct);
    initShipCalc(root);
    initEmbSimulator(root);
    $$('[data-facets-root]', root).forEach(initFacets);
    Wishlist.paint(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initSearch();
    Cart.init();
    Cart.initExtras();
    Wishlist.init();
    initQuickAdd();
    initSection(document);
  });

  // Editor de temas
  document.addEventListener('shopify:section:load', (e) => {
    initSection(e.target);
    if (e.target.querySelector('[data-header]')) initHeader();
    if (e.target.querySelector('[data-drawer-overlay]')) Cart.overlay = $('[data-drawer-overlay]');
  });

  window.DutturiCart = Cart;
  window.DutturiWishlist = Wishlist;
})();
